/**
 * workshopCreateSubmit — the Create Workshop wizard's submit machinery + the
 * blank-draft factory, extracted from useWorkshopCreateForm to keep that file
 * under the size cap. Builds the POST payload, routes server field errors back
 * to the owning step, and provides the "create & add another" reset (B3).
 */
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';

import { WorkshopValidationError, type WorkshopDetail } from '../../../../api/workshops';

import type { useCreateWorkshop } from './useCreateWorkshop';
import type { WorkshopDraft } from './useWorkshopCreateForm';
import { occurrenceStartsFor, recurrenceFromSchedule } from './workshopCreateSeries';
import { toIso } from './workshopCreateValidation';

export type SubmitMode = 'navigate' | 'addAnother';

/** Which step each server field error maps to, so a rejected submit routes
 *  focus back. Layout: 0 Location · 1 Workshop · 2 Schedule · 3 Review.
 *  There is no Teacher step (BON-466). */
const FIELD_STEP: Record<string, number> = {
  regionId: 0,
  venueId: 0,
  workshopTypeId: 1,
  capacity: 1,
  priceCents: 1,
  startsAt: 2,
  endsAt: 2,
  recurrence: 2,
  cadence: 2,
  untilDate: 2,
  occurrenceCount: 2,
  occurrenceStarts: 2,
  seriesDates: 2,
};

/** The API reports the series block's own fields as `recurrence.<field>`; the
 *  drawer renders them on the plain field, so the prefix comes off here. */
function leafField(field: string): string {
  return field.startsWith('recurrence.') ? field.slice('recurrence.'.length) : field;
}

/** The step owning a server field error. */
function stepForField(field: string): number {
  return FIELD_STEP[field] ?? REVIEW_STEP;
}

/** Index of the Review step — fallback owner for an unmapped server field. */
const REVIEW_STEP = 3;

/** A blank draft scoped to `regionId` + (optional) pre-set `venueId` — both are
 *  kept across an "add another" reset. */
export function emptyDraft(regionId: string, venueId = ''): WorkshopDraft {
  return {
    regionId,
    workshop: { workshopTypeId: '', capacity: '', price: '', durationMinutes: null },
    schedule: {
      venueId,
      date: '',
      start: '',
      end: '',
      repeat: 'none',
      endMode: 'until',
      untilDate: '',
      occurrenceCount: '',
      seriesEdits: {},
    },
  };
}

interface RunSubmitArgs {
  draft: WorkshopDraft;
  mutation: ReturnType<typeof useCreateWorkshop>;
  /** On success — navigate to the new detail page, or reset for the next. */
  onDone: (workshop: WorkshopDetail) => void;
  setFieldErrors: (m: Record<string, string>) => void;
  setSubmitError: (s: string | null) => void;
  setStep: (i: number) => void;
}

async function runSubmit(args: RunSubmitArgs): Promise<void> {
  const { draft, mutation, onDone, setFieldErrors, setSubmitError, setStep } = args;
  setSubmitError(null);
  setFieldErrors({});
  const recurrence = recurrenceFromSchedule(draft.schedule);
  const occurrenceStarts = occurrenceStartsFor(draft.schedule);
  try {
    const created = await mutation.mutateAsync({
      venueId: draft.schedule.venueId,
      workshopTypeId: draft.workshop.workshopTypeId,
      startsAt: toIso(draft.schedule.date, draft.schedule.start),
      endsAt: toIso(draft.schedule.date, draft.schedule.end),
      capacity: Number(draft.workshop.capacity),
      priceCents: Math.round(Number(draft.workshop.price) * 100),
      ...(recurrence
        ? { recurrence: { ...recurrence, ...(occurrenceStarts ? { occurrenceStarts } : {}) } }
        : {}),
    });
    onDone(created);
  } catch (err) {
    if (err instanceof WorkshopValidationError) {
      const fieldErrors = Object.fromEntries(
        Object.entries(err.fieldErrors).map(([field, message]) => [leafField(field), message]),
      );
      setFieldErrors(fieldErrors);
      const firstStep = Object.keys(fieldErrors)
        .map(stepForField)
        .sort((a, b) => a - b)[0];
      if (firstStep !== undefined) setStep(firstStep);
    } else {
      setSubmitError((err as Error).message);
    }
  }
}

interface MakeSubmitArgs {
  draft: WorkshopDraft;
  mutation: ReturnType<typeof useCreateWorkshop>;
  /** Whether to carry region / venue into the next "add another" draft (kept
   *  when their Location mode is `fixed` or `prefill`; cleared when `open`). */
  keepRegion: boolean;
  keepVenue: boolean;
  onCreated: (workshop: WorkshopDetail) => void;
  initialDraft: MutableRefObject<WorkshopDraft>;
  setDraft: Dispatch<SetStateAction<WorkshopDraft>>;
  setFieldErrors: Dispatch<SetStateAction<Record<string, string>>>;
  setSubmitError: Dispatch<SetStateAction<string | null>>;
  setStep: Dispatch<SetStateAction<number>>;
  setConfirmation: Dispatch<SetStateAction<string | null>>;
}

/** The post-create banner: one draft, or the whole series. */
function confirmationFor(created: WorkshopDetail): string {
  const total = created.series?.total;
  return total && total > 1
    ? `${total} drafts created — start another below.`
    : 'Draft created — start another below.';
}

/** Build the `submit(mode)` callback + its "add another" reset (B3 re-baseline). */
export function makeSubmit(a: MakeSubmitArgs) {
  const resetForNext = (created: WorkshopDetail) => {
    const fresh = emptyDraft(
      a.keepRegion ? a.draft.regionId : '',
      a.keepVenue ? a.draft.schedule.venueId : '',
    );
    a.initialDraft.current = fresh;
    a.setDraft(fresh);
    a.setFieldErrors({});
    a.setSubmitError(null);
    a.setStep(0); // back to the Location step
    a.setConfirmation(confirmationFor(created));
  };
  return (mode: SubmitMode = 'navigate') => {
    a.setConfirmation(null);
    void runSubmit({
      draft: a.draft,
      mutation: a.mutation,
      onDone: mode === 'addAnother' ? resetForNext : a.onCreated,
      setFieldErrors: a.setFieldErrors,
      setSubmitError: a.setSubmitError,
      setStep: a.setStep,
    });
  };
}
